'use strict';

angular.module('ClinicApp').config(function($stateProvider, authorizationProvider) {
  $stateProvider.state('reports', {
    url: '/reports/?clinicId&year&month',
    templateUrl: '/views/reports.html',
    controller: 'ReportsController',
    reloadOnSearch: false,
    resolve: {
      auth: authorizationProvider.requireAuthorizedUser,
      pageTitle: function($pageTitle) {
        $pageTitle.set('Reports - Opencare');
      },
      user: function(CurrentUser) {
        return CurrentUser.waitForUserLoad();
      },
    },
  });
});

angular.module('ClinicApp').controller('ReportsController', function($scope, $stateParams, $timeout, $window, CurrentUser, ClinicService) {
  const PRESET_PARAMS = ['clinicId', 'year', 'month'];
  const PERIOD_MONTHS_AHEAD = 6;
  const MINUTES_IN_HOUR = 60;

  $scope.FIELD_NAMES = {
    PRACTICE: 'Practice',
    PERIOD: 'Period',
    TOTAL_REQUESTS: 'Total requests',
    SCHEDULED_APPOINTMENTS: 'Scheduled appointments',
    PATIENTS_SEEN: 'Patients seen',
    SCHEDULE_RATE: 'Schedule rate',
    CANCELLATION_RATE: 'Cancellation rate',
    NO_SHOW_RATE: 'No show rate',
    AVG_RESPONSE_TIME: 'Average response time',
  };

  $scope.loading = false;
  $scope.performance = {
    summaryMetrics: [],
    clinicMetrics: [],
  };

  $scope.csvControl = {
    filename: 'Opencare Performance Metrics.csv',
    getHeader: () => [
      $scope.FIELD_NAMES.PRACTICE,
      $scope.FIELD_NAMES.PERIOD,
      $scope.FIELD_NAMES.TOTAL_REQUESTS,
      $scope.FIELD_NAMES.SCHEDULED_APPOINTMENTS,
      $scope.FIELD_NAMES.PATIENTS_SEEN,
      $scope.FIELD_NAMES.SCHEDULE_RATE,
      $scope.FIELD_NAMES.CANCELLATION_RATE,
      $scope.FIELD_NAMES.NO_SHOW_RATE,
      $scope.FIELD_NAMES.AVG_RESPONSE_TIME,
    ],
    getDataList: () => {
      const periods = [...$scope.getFilteredPeriods()]
        .sort((a, b) => getPeriodKey(a).localeCompare(getPeriodKey(b)))
        .map((period) => moment().year(period.year)
          .month(period.month - 1)
          .format('MMMM YYYY'));

      return $scope.performance.clinicMetrics.map((metrics) => ({
        [$scope.FIELD_NAMES.PRACTICE]: metrics.clinic.name,
        [$scope.FIELD_NAMES.PERIOD]: periods.join(', '),
        [$scope.FIELD_NAMES.TOTAL_REQUESTS]: $scope.formatInteger(metrics.num_appointments),
        [$scope.FIELD_NAMES.SCHEDULED_APPOINTMENTS]: $scope.formatInteger(metrics.num_scheduled),
        [$scope.FIELD_NAMES.PATIENTS_SEEN]: $scope.formatInteger(metrics.num_visited),
        [$scope.FIELD_NAMES.SCHEDULE_RATE]: $scope.formatPercents(metrics.schedule_rate),
        [$scope.FIELD_NAMES.CANCELLATION_RATE]: $scope.formatPercents(metrics.cancel_rate),
        [$scope.FIELD_NAMES.NO_SHOW_RATE]: $scope.formatPercents(metrics.no_show_rate),
        [$scope.FIELD_NAMES.AVG_RESPONSE_TIME]: $scope.formatHoursDuration(metrics.avg_response_hours),
      }));
    },
  };

  $scope.formatPercents = (value) => {
    if (typeof value !== 'number') {
      return 'N/A';
    }
    const valuePercents = value * 100.0;
    const fractionDigits = Math.floor(valuePercents) === valuePercents ? 0 : 1;

    return valuePercents.toFixed(fractionDigits) + '%';
  };

  $scope.formatInteger = (value) => {
    return typeof value === 'number' ? Math.round(value) : 'N/A';
  };

  $scope.formatHoursDuration = (value) => {
    if (typeof value !== 'number') {
      return 'N/A';
    }

    const hours = Math.floor(value);
    if (hours === value) {
      return `${hours}h`;
    }

    const fractionalPart = value - hours;
    const minutes = Math.round(MINUTES_IN_HOUR * fractionalPart);
    return hours === 0 ? `${minutes}min` : `${hours}h ${minutes}min`;
  };

  $scope.getFilteredClinics = () => {
    return Array.isArray($scope.filters.clinic) ? $scope.filters.clinic : [$scope.filters.clinic];
  };

  $scope.getFilteredPeriods = () => {
    return Array.isArray($scope.filters.period) ? $scope.filters.period : [$scope.filters.period];
  };

  $scope.resetFilters = () => {
    if ($scope.loading) {
      return;
    }
    $scope.loading = true;

    return loadPerformanceMetrics().finally(() => {
      $scope.loading = false;
    });
  };

  $scope.print = () => $window.print();

  function setupFilters() {
    const clinics = initializeClinics();
    const periods = initializePeriods(clinics);

    const filtersPreset = getFiltersPreset();
    const presetClinic = getPresetClinic(clinics, filtersPreset);
    const presetPeriod = getPresetPeriod(periods, filtersPreset);

    $scope.clinics = clinics;
    $scope.periodMonthsByYear = periods.reduce((result, period) => {
      result[period.year] = result[period.year] || [];
      result[period.year].push(period.month);
      return result;
    }, {});
    $scope.filters = {
      clinic: presetClinic,
      period: presetPeriod,
    };
  }

  function initializeClinics() {
    return (CurrentUser.getClinics() || []).sort((a, b) => a.name.localeCompare(b.name));
  }

  function initializePeriods(clinics) {
    const [firstCreatedClinic] = [...clinics].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
    const startPeriod = firstCreatedClinic ? moment(firstCreatedClinic.createdAt) : null;
    const endPeriod = moment().add(PERIOD_MONTHS_AHEAD, 'months');
    const periods = [];

    while (startPeriod?.isSameOrBefore(endPeriod, 'month')) {
      periods.push({
        year: startPeriod.year(),
        month: startPeriod.month() + 1,
      });
      startPeriod.add(1, 'month');
    }

    return periods;
  }

  function getFiltersPreset() {
    return PRESET_PARAMS.reduce((result, param) => {
      result[param] = parseInt($stateParams[param], 10) || undefined;
      return result;
    }, {});
  }

  function getPresetClinic(clinics, filtersPreset) {
    let presetClinic = clinics.find((clinic) => clinic.id === filtersPreset.clinicId);
    if (!presetClinic && clinics.length) {
      presetClinic = clinics[0];
    }
    return presetClinic;
  }

  function getPresetPeriod(periods, filtersPreset) {
    let presetPeriod = periods.find((period) => {
      return period.year === filtersPreset.year && period.month === filtersPreset.month;
    });
    if (!presetPeriod && periods.length) {
      const currentMonthPeriod = periods.find((period) => {
        return period.year === moment().year() && period.month === moment().month() + 1;
      });
      presetPeriod = currentMonthPeriod || periods[0];
    }
    return presetPeriod;
  }

  function getPeriodKey(period) {
    return `${period.year}-${_.padLeft(period.month, 2, '0')}`;
  }

  function resetPerformanceSummaryMetrics(performance) {
    $scope.performance.summaryMetrics = [{
      label: $scope.FIELD_NAMES.TOTAL_REQUESTS,
      value: $scope.formatInteger(performance?.summary?.num_appointments),
    }, {
      label: $scope.FIELD_NAMES.SCHEDULED_APPOINTMENTS,
      value: $scope.formatInteger(performance?.summary?.num_scheduled),
    }, {
      label: $scope.FIELD_NAMES.PATIENTS_SEEN,
      value: $scope.formatInteger(performance?.summary?.num_visited),
    }, {
      label: $scope.FIELD_NAMES.SCHEDULE_RATE,
      value: $scope.formatPercents(performance?.summary?.schedule_rate),
    }, {
      label: $scope.FIELD_NAMES.CANCELLATION_RATE,
      value: $scope.formatPercents(performance?.summary?.cancel_rate),
    }, {
      label: $scope.FIELD_NAMES.NO_SHOW_RATE,
      value: $scope.formatPercents(performance?.summary?.no_show_rate),
    }, {
      label: $scope.FIELD_NAMES.AVG_RESPONSE_TIME,
      value: $scope.formatHoursDuration(performance?.summary?.avg_response_hours),
    }];
  }

  function loadPerformanceMetrics() {
    const filteredClinics = $scope.getFilteredClinics();
    const filteredPeriods = $scope.getFilteredPeriods();
    const clinicIds = filteredClinics.map((clinic) => clinic.id);
    const periods = filteredPeriods.map(getPeriodKey);

    return ClinicService.getPerformanceMetrics(clinicIds, periods).then((performance) => {
      resetPerformanceSummaryMetrics(performance);

      $scope.performance.clinicMetrics = performance.metrics.map((metrics) => {
        metrics.clinic = filteredClinics.find((clinic) => clinic.id === metrics.id);
        return metrics;
      }).sort((a, b) => a.clinic.name.localeCompare(b.clinic.name));
    });
  }

  setupFilters();
  resetPerformanceSummaryMetrics();

  $timeout($scope.resetFilters);
});
